import React, { useContext, useEffect, useState } from 'react'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import { useNavigate } from 'react-router-dom'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import { Box, Typography } from '@mui/material'

// API services
import {
  getFilteredLeaveApproval,
  getFilteredLeaveApprovalTotal,
  useGetAllFilteredLeaveApproval,
} from 'services/api/modules/leave/leaveApproval'

import LeavePendingTab from 'pages/approval-center/components/leave/LeavePendingTab'
import LeaveUpcomingTab from 'pages/approval-center/components/leave/LeaveUpcomingTab'
import LeaveRespondedTab from 'pages/approval-center/components/leave/LeaveRespondedTab'
import LeaveSummaryTab from 'pages/approval-center/components/leave/LeaveSummaryTab'
import { authContext } from 'contexts/AuthContext'
import { useQuery } from 'react-query'

const logLeaveRespondedDebug = (label, payload, request) => {
  if (process.env.NODE_ENV !== 'development') {
    return
  }

  const rows = payload?.data

  console.groupCollapsed(`[leave-approval responded] ${label}`)
  console.log('request', request)
  console.log('rowCount', Array.isArray(rows) ? rows.length : null)
  console.log('total', payload?.total)
  console.log('pagination', payload?.pagination)
  console.log(
    'rowIds',
    Array.isArray(rows) ? rows.slice(0, 10).map((item) => item?.id) : null
  )
  console.log('payload', payload)
  console.groupEnd()
}

const LeaveApprovalCenterOverview = ({ initialTab, module }) => {
  const {
    auth: {
      data: { permissions },
    },
  } = useContext(authContext)

  // change state from child to parent
  const [tabIndex, setTabIndex] = useState(initialTab)
  const [respondedPage, setRespondedPage] = useState(0)
  const [respondedRowsPerPage, setRespondedRowsPerPage] = useState(5)

  const { data: pendingData } = useGetAllFilteredLeaveApproval({
    status: 'pending',
  })
  const {
    data: respondedData,
    isLoading: respondedIsLoading,
    error: respondedError,
  } = useQuery(
    ['leave-approval-responded', respondedPage + 1, respondedRowsPerPage],
    async () => {
      const request = {
        status: 'responded',
        page: respondedPage + 1,
        limit: respondedRowsPerPage,
        sortBy: 'updated_at',
        sortOrder: 'desc',
      }
      const res = await getFilteredLeaveApproval(request)
      logLeaveRespondedDebug('page request', res.data, request)

      return res.data
    },
    {
      keepPreviousData: true,
    }
  )
  const { data: respondedTotalData } = useQuery(
    ['leave-approval-responded-total'],
    async () => {
      const request = {
        status: 'responded',
      }
      const res = await getFilteredLeaveApprovalTotal(request)
      logLeaveRespondedDebug('total request', res.data, request)

      return res.data
    }
  )
  const respondedRequest = respondedData?.data
  const displayedRespondedRequest = respondedRequest?.slice(0, respondedRowsPerPage)
  const respondedRawPagination = respondedData?.pagination || respondedData
  const respondedCount =
    respondedTotalData?.total ??
    respondedTotalData?.pagination?.total ??
    respondedTotalData?.data?.length ??
    respondedRawPagination?.total ??
    respondedData?.total ??
    respondedRequest?.length
  const respondedPagination = respondedRawPagination
    ? {
        ...respondedRawPagination,
        total: respondedCount,
      }
    : undefined

  // useEffect
  useEffect(() => {
    changeRoute(tabIndex)
  }, [tabIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  // function to change route based on tabIndex
  const navigate = useNavigate()
  const changeRoute = (index) => {
    switch (index) {
      case 0:
        navigate(`/app/approval-center/leave/pending`)
        break
      case 1:
        navigate(`/app/approval-center/leave/upcoming`)
        break
      case 2:
        navigate(`/app/approval-center/leave/responded`)
        break
      case 3:
        navigate(`/app/approval-center/leave/summary`)
        break

      default:
        break
    }
  }

  const handleRespondedPageChange = (event, newPage) => {
    setRespondedPage(newPage)
  }

  const handleRespondedRowsPerPageChange = (event) => {
    setRespondedRowsPerPage(parseInt(event.target.value, 10))
    setRespondedPage(0)
  }

  return (
    <DashboardContainer
      headerTitle={
        <Typography fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
          {module}
        </Typography>
      }
    >
      <Box
        sx={{
          bgcolor: 'white.main',
          border: 1,
          borderColor: 'gray-line.main',
          borderRadius: '4.5px',
        }}
      >
        <AdxTabsBadge
          scrollButtons={false}
          sxPanel={{
            borderRadius: 0,
            overflowY: 'unset',
            boxSizing: 'border-box',
          }}
          setTabIndex={setTabIndex}
          initialValue={initialTab}
          whiteBackground
          sxTabs={{}}
        >
          <AdxTabsPanel label="Pending" notification={pendingData?.total}>
            <LeavePendingTab />
          </AdxTabsPanel>
          <AdxTabsPanel label="Pending Pre-Approval">
            <LeaveUpcomingTab />
          </AdxTabsPanel>
          <AdxTabsPanel label="Approved">
            <LeaveRespondedTab
              respondedData={displayedRespondedRequest}
              isLoading={respondedIsLoading}
              error={respondedError}
              pagination={respondedPagination}
              page={respondedPage}
              rowsPerPage={respondedRowsPerPage}
              onPageChange={handleRespondedPageChange}
              onRowsPerPageChange={handleRespondedRowsPerPageChange}
            />
          </AdxTabsPanel>
          {permissions?.includes(2) && (
            <AdxTabsPanel label="Summary">
              <LeaveSummaryTab />
            </AdxTabsPanel>
          )}
        </AdxTabsBadge>
      </Box>
    </DashboardContainer>
  )
}

export default LeaveApprovalCenterOverview
